import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { NotebookPen, ReceiptText, RotateCcw } from 'lucide-react'
import { useState, useEffect } from 'react'
import { BusinessModel } from '@/http/models/business/Business'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { TextInput } from '@/components/atoms/inputs'
import { Spinner } from '@/components/ui/spinner'
import { PosDevices } from './PosDevices'
import { TillDevices } from './TillDevices'
import { ReceiptPreview } from './ReceiptPreview'
import { RECEIPT_OPTION_DEFAULTS, RECEIPT_SWITCHES, type ReceiptOptions } from '@iws/pos-devices'
import { Switch } from '@/components/ui/switch'
import { getFileUrl } from '@/clients/http'

interface PosSettingsProps {
    businessProfile: any | null
    onRefresh: () => Promise<void>
}

const withoutFeatureFlags = (settings: Record<string, unknown> | undefined) => {
    const { feature_flags: _ignored, ...rest } = settings ?? {}
    return rest
}

/** Days a POS sale can be returned when the store sets none (POS.md O9); same default as the server and the till. */
const DEFAULT_RETURN_WINDOW_DAYS = 7

/**
 * Settings the POS apps read (`app_settings.pos`).
 * - Offline credit allowance: how much credit a till may give a customer before it syncs again (POS.md §15.4); a
 *   customer's own allowance overrides it. Default 0: no credit offline beyond what the server has confirmed (O8).
 * - Return window: how many days after a sale it can be returned (O9). Tills keep orders longer than this.
 */
export const PosSettings = ({ businessProfile, onRefresh }: PosSettingsProps) => {
    const [isSaving, setIsSaving] = useState(false)
    const [receipt, setReceipt] = useState<ReceiptOptions>(RECEIPT_OPTION_DEFAULTS)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            offline_credit_allowance: '0',
            return_window_days: String(DEFAULT_RETURN_WINDOW_DAYS),
            receipt_footer: '',
        },
    })

    useEffect(() => {
        const pos = businessProfile?.app_settings?.pos
        reset({
            offline_credit_allowance: String(pos?.offline_credit_allowance ?? 0),
            return_window_days: String(pos?.return_window_days ?? DEFAULT_RETURN_WINDOW_DAYS),
            receipt_footer: pos?.receipt_footer ?? '',
        })
        setReceipt({ ...RECEIPT_OPTION_DEFAULTS, ...(pos?.receipt ?? {}) })
    }, [businessProfile, reset])

    // The till has no media URL helper, so the logo's full URL is saved with the options.
    const logoUrl = businessProfile?.logo_path ? getFileUrl(businessProfile.logo_path) : ''

    const onSubmit = async (data: { offline_credit_allowance: string; return_window_days: string; receipt_footer: string }) => {
        const allowanceRaw = String(data.offline_credit_allowance).trim()
        const allowance = allowanceRaw === '' ? 0 : Number(allowanceRaw)
        if (Number.isNaN(allowance) || allowance < 0) {
            toast.error('Enter a credit allowance of 0 or more')
            return
        }
        const windowRaw = String(data.return_window_days).trim()
        const returnWindowDays = windowRaw === '' ? DEFAULT_RETURN_WINDOW_DAYS : Number(windowRaw)
        if (!Number.isInteger(returnWindowDays) || returnWindowDays < 0) {
            toast.error('Enter the return window as whole days, 0 or more')
            return
        }
        setIsSaving(true)
        try {
            const response = await BusinessModel.updateBusinessProfile({
                body: {
                    // Feature flags are System Admin's (POS.md O10): never sent back from here.
                    app_settings: {
                        ...withoutFeatureFlags(businessProfile?.app_settings),
                        pos: {
                            ...businessProfile?.app_settings?.pos,
                            offline_credit_allowance: allowance,
                            return_window_days: returnWindowDays,
                            receipt_footer: data.receipt_footer.trim(),
                            receipt: { ...receipt, logo_url: logoUrl || undefined },
                        },
                    },
                },
            })
            if (response.success) {
                toast.success('POS settings updated')
                await onRefresh()
            } else {
                toast.error('Failed to update settings')
            }
        } catch (error) {
            toast.error('An error occurred')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="space-y-4">
            <PosDevices businessProfile={businessProfile} />
            <TillDevices />

            <Card>
                <CardHeader>
                    <CardTitle>Offline credit</CardTitle>
                    <CardDescription>
                        How much credit a POS device may give one customer before it syncs again. A customer's own
                        allowance, set on their page, overrides this. At 0, a device gives no credit while offline.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <TextInput
                            control={control}
                            name="offline_credit_allowance"
                            label="Allowance per customer"
                            placeholder="0"
                            icon={<NotebookPen className="size-4" />}
                            errors={errors}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Returns</CardTitle>
                    <CardDescription>
                        How many days after a sale the POS accepts a return. Devices keep sales for longer than this.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <TextInput
                            control={control}
                            name="return_window_days"
                            label="Return window (days)"
                            placeholder={String(DEFAULT_RETURN_WINDOW_DAYS)}
                            icon={<RotateCcw className="size-4" />}
                            errors={errors}
                        />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Receipts</CardTitle>
                    <CardDescription>
                        What every POS receipt shows, and the line printed at the bottom. An empty footer prints “Thank you for shopping!”. Saved with Save below.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <TextInput
                        control={control}
                        name="receipt_footer"
                        label="Receipt footer"
                        placeholder="Thank you for shopping!"
                        icon={<ReceiptText className="size-4" />}
                        errors={errors}
                    />
                    <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-ink">Shown on the receipt</p>
                        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
                            {RECEIPT_SWITCHES.map((sw) => (
                                <label key={sw.key} className="flex items-center justify-between gap-3 text-sm text-text-2">
                                    <span>
                                        {sw.label}
                                        {sw.key === 'show_logo' && !logoUrl && <span className="ml-1 text-xs text-muted-foreground">(set a logo in General)</span>}
                                    </span>
                                    <Switch checked={!!receipt[sw.key]} onCheckedChange={(v) => setReceipt((r) => ({ ...r, [sw.key]: v }))} />
                                </label>
                            ))}
                        </div>
                        {receipt.show_logo && (
                            <p className="text-xs text-muted-foreground">The logo prints through the browser print dialog; thermal printers skip it for now.</p>
                        )}
                    </div>
                    <div className="mt-4 space-y-1.5">
                        <p className="text-sm font-medium text-ink">Preview</p>
                        <ReceiptPreview
                            options={{ ...receipt, logo_url: logoUrl || undefined }}
                            shop={businessProfile ?? {}}
                            footer={watch('receipt_footer') ?? ''}
                            returnDays={Number(watch('return_window_days')) || DEFAULT_RETURN_WINDOW_DAYS}
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                    {isSaving && <Spinner />}
                    Save
                </Button>
            </div>
        </div>
    )
}
